export type FormValue<T = unknown> =
  | T
  | string
  | number
  | boolean
  | null
  | undefined
  | { [key: string]: FormValue<T> }
  | FormValue<T>[]

export interface ValidationRule<T = unknown> {
  required?: boolean
  textError?: string
  minItems?: number
  maxItems?: number
  validate?: (value: FormValue<T>) => boolean | string
}

export interface ValidationConfig<T = unknown> {
  [fieldPath: string]: ValidationRule<T>
}

export interface ValidationResult {
  fieldErrors: Record<string, ValidationRule>
  tabErrors: Record<number, string[]>
  isValid: boolean
}

function checkEmpty<T>(value: FormValue<T>): boolean {
  return (
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' &&
      !Array.isArray(value) &&
      !(value instanceof Date) &&
      Object.keys(value).length === 0)
  )
}

function getNestedValue<T>(obj: FormValue<T>, path: string): FormValue<T> {
  return path.split('.').reduce((current: FormValue<T>, key: string) => {
    if (Array.isArray(current) && !Number.isNaN(parseInt(key, 10))) {
      return current[parseInt(key, 10)]
    }

    if (
      current &&
      typeof current === 'object' &&
      !Array.isArray(current) &&
      key in current
    ) {
      return (current as Record<string, FormValue<T>>)[key]
    }

    return undefined
  }, obj)
}

function hasWildcard(path: string): boolean {
  return path.includes('*')
}

interface WildcardMatch<T = unknown> {
  path: string
  value: FormValue<T>
}

function getWildcardValues<T>(
  obj: FormValue<T>,
  path: string,
): WildcardMatch<T>[] {
  const parts = path.split('.')
  const results: WildcardMatch<T>[] = []

  function traverse(
    current: FormValue<T>,
    currentPath: string[],
    index: number,
  ) {
    if (index >= parts.length) {
      results.push({ path: currentPath.join('.'), value: current })
      return
    }

    const part = parts[index]

    if (part === '*') {
      if (Array.isArray(current)) {
        current.forEach((item: FormValue<T>, i: number) => {
          traverse(item, [...currentPath, i.toString()], index + 1)
        })
      } else if (typeof current === 'object' && current !== null) {
        Object.keys(current).forEach((key: string) => {
          traverse(
            (current as Record<string, FormValue<T>>)[key],
            [...currentPath, key],
            index + 1,
          )
        })
      }
    } else if (current && typeof current === 'object') {
      if (Array.isArray(current) && !Number.isNaN(parseInt(part, 10))) {
        traverse(current[parseInt(part, 10)], [...currentPath, part], index + 1)
      } else if (!Array.isArray(current) && part in current) {
        traverse(
          (current as Record<string, FormValue<T>>)[part],
          [...currentPath, part],
          index + 1,
        )
      }
    }
  }

  traverse(obj, [], 0)
  return results
}

export function validateFormData<T extends object>(
  formData: T,
  config: ValidationConfig = {},
  fieldToTabMap?: Record<string, number>,
): ValidationResult {
  const fieldErrors: Record<string, ValidationRule> = {}
  const tabErrors: Record<number, string[]> = {}

  Object.entries(config).forEach(([fieldPath, rules]) => {
    let hasError = false
    let errorMessage = rules.textError || ''

    if (hasWildcard(fieldPath)) {
      const matches = getWildcardValues(formData, fieldPath)

      if (matches.length === 0 && rules.required) {
        hasError = true
        errorMessage = `Поле обязательно (путь: ${fieldPath})`
      } else {
        matches.forEach((match) => {
          let isMatchError = false

          if (rules.validate) {
            const validationResult = rules.validate(match.value)
            if (validationResult !== true) {
              isMatchError = true
              errorMessage = (validationResult as string) || errorMessage
            }
          } else if (rules.required && checkEmpty(match.value)) {
            isMatchError = true
          }

          if (isMatchError) {
            fieldErrors[match.path] = { ...rules, textError: errorMessage }

            if (fieldToTabMap && fieldToTabMap[fieldPath]) {
              const tabNumber = fieldToTabMap[fieldPath]
              tabErrors[tabNumber] = tabErrors[tabNumber] || []
              if (!tabErrors[tabNumber].includes(match.path)) {
                tabErrors[tabNumber].push(match.path)
              }
            }
          }
        })
        return
      }
    } else {
      const value = getNestedValue(formData, fieldPath)
      let isValueError = false

      if (rules.validate) {
        const validationResult = rules.validate(value)
        if (validationResult !== true) {
          isValueError = true
          errorMessage = (validationResult as string) || errorMessage
        }
      } else if (rules.required && checkEmpty(value)) {
        isValueError = true
      }

      if (Array.isArray(value) && !isValueError) {
        if (rules.minItems !== undefined && value.length < rules.minItems) {
          isValueError = true
          errorMessage =
            rules.textError || `Минимальное количество: ${rules.minItems}`
        }

        if (rules.maxItems !== undefined && value.length > rules.maxItems) {
          isValueError = true
          errorMessage =
            rules.textError || `Максимальное количество: ${rules.maxItems}`
        }
      }

      hasError = isValueError
    }

    if (hasError) {
      fieldErrors[fieldPath] = { ...rules, textError: errorMessage }

      if (fieldToTabMap && fieldToTabMap[fieldPath]) {
        const tabNumber = fieldToTabMap[fieldPath]
        tabErrors[tabNumber] = tabErrors[tabNumber] || []
        if (!tabErrors[tabNumber].includes(fieldPath)) {
          tabErrors[tabNumber].push(fieldPath)
        }
      }
    }
  })

  return {
    fieldErrors,
    tabErrors,
    isValid: Object.keys(fieldErrors).length === 0,
  }
}
